import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { faTrash } from "@fortawesome/pro-solid-svg-icons/faTrash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PopConfirm from "antd/es/popconfirm";
import { useIntl } from "react-intl";

import EntityType from "@mapmycustomers/shared/enum/EntityType";
import Activity from "@mapmycustomers/shared/types/entity/Activity";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";

import RoundButton from "@app/component/RoundButton";
import useView from "@app/component/view/utils/useView";
import localSettings from "@app/config/LocalSettings";
import ButtonType from "@app/enum/ButtonType";
import { activityListViewAnalytics } from "@app/scene/activity/activityTableAnalytics";
import { bulkDeleteActivities } from "@app/scene/activity/store/actions";
import { getBulkDeleteLoading } from "@app/scene/activity/store/selectors";
import { RootState } from "@app/store/rootReducer";
import { DEFAULT_PAGE_SIZE } from "@app/util/consts";

import styles from "./BulkDeleteButton.module.scss";
import DeleteActivitiesConfirmationModal from "./DeleteActivitiesConfirmationModal";

interface Props {
  bulkDeleteActivities: typeof bulkDeleteActivities.request;
  bulkDeleteLoading: boolean;
  className?: string;
  selected: Activity[];
}

const BulkDeleteButton: React.FC<Props> = ({
  bulkDeleteActivities,
  bulkDeleteLoading,
  className,
  selected,
}) => {
  const { updateViewState } = useView<Activity>();
  const [isModalVisible, showModal, hideModal] = useBoolean();
  const [isPopupVisible, showPopup, hidePopup] = useBoolean();
  const savedDontShowAgain = localSettings.getDontShowDeleteEntityConfirmation(EntityType.ACTIVITY);
  const intl = useIntl();
  const hasRecurringInSelected = useMemo(
    () => selected.some((activity) => activity.recurInterval),
    [selected]
  );
  const onConfirm = useCallback(
    (dontShowAgain?: boolean) => {
      const closeModalOnSuccess = () => {
        if (dontShowAgain) {
          localSettings.setDontShowDeleteEntityConfirmation(EntityType.ACTIVITY);
        }
        updateViewState({ range: { endRow: DEFAULT_PAGE_SIZE, startRow: 0 } });
        hideModal();
      };
      bulkDeleteActivities({
        ids: selected.map(({ id }) => id),
        onSuccess: hasRecurringInSelected ? closeModalOnSuccess : hidePopup,
      });
    },
    [bulkDeleteActivities, hasRecurringInSelected, hideModal, hidePopup, selected, updateViewState]
  );
  const onPopupConfirm = useCallback(() => {
    onConfirm();
  }, [onConfirm]);
  const onDelete = useCallback(() => {
    if (hasRecurringInSelected && !savedDontShowAgain) {
      showModal();
    } else {
      showPopup();
    }
    activityListViewAnalytics.clicked(["Checkbox to Select All", "Delete"]);
  }, [hasRecurringInSelected, savedDontShowAgain, showModal, showPopup]);

  if (selected.length === 0) {
    return null;
  }

  return (
    <>
      <PopConfirm
        cancelText={intl.formatMessage({
          id: "activity.listView.bulkDelete.popConfirm.cancel",
          defaultMessage: "Cancel",
          description: "Delete popConfirm cancel label",
        })}
        okButtonProps={{
          danger: true,
          disabled: bulkDeleteLoading,
          loading: bulkDeleteLoading,
          type: "default",
        }}
        okText={intl.formatMessage({
          id: "activity.listView.bulkDelete.popConfirm.delete",
          defaultMessage: "Delete",
          description: "Delete popConfirm confirm label",
        })}
        onCancel={hidePopup}
        onConfirm={onPopupConfirm}
        open={isPopupVisible}
        overlayClassName={styles.confirmOverlay}
        placement="bottomLeft"
        title={
          <div className={styles.popConfirmTitleContainer}>
            {intl.formatMessage(
              {
                id: "activity.listView.bulkDelete.popConfirm.title",
                defaultMessage:
                  "<b>Are you sure?</b> You have the option to restore deleted records for up to 90 days.",
                description: "Delete popconfirm text",
              },
              { b: (text) => <b>{text}</b> }
            )}
          </div>
        }
      >
        <RoundButton
          className={className}
          leftIcon={<FontAwesomeIcon icon={faTrash} />}
          loading={bulkDeleteLoading}
          onClick={onDelete}
          type={ButtonType.DANGER}
        >
          {intl.formatMessage({
            id: "activity.listView.bulkDelete.deleteActivities",
            defaultMessage: "Delete",
            description: "Label for delete activities button",
          })}
        </RoundButton>
      </PopConfirm>
      {isModalVisible && (
        <DeleteActivitiesConfirmationModal
          loading={bulkDeleteLoading}
          onConfirm={onConfirm}
          onHide={hideModal}
        />
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  bulkDeleteLoading: getBulkDeleteLoading(state),
});

const mapDispatchToProps = {
  bulkDeleteActivities: bulkDeleteActivities.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(BulkDeleteButton);
